import { ThesisFileType, ThesisType } from '@rdm/db'

export const getThesisTypeFileCode = (thesisType: ThesisType): string => {
  switch (thesisType) {
    case 'PHD':
      return 'phd'
    case 'PROFESSIONAL_DOCTORATE':
      return 'profdoc'
    case 'MPHIL':
    case 'MPHIL_IN_LIEU_OF_PHD':
    default:
      return 'mphil'
  }
}

const getRenamedThesisDocumentFilename = (
  filename: string,
  eduPersonPrincipalName: string,
  typeCode: string,
): string => {
  const fileExtension = filename.split('.').pop()
  const username = eduPersonPrincipalName.split('@')[0]
  return `${username}_${typeCode}_thesis.${fileExtension}`
}

const getRenamedThesisIthenticateFilename = (
  filename: string,
  eduPersonPrincipalName: string,
  typeCode: string,
): string => {
  const fileExtension = filename.split('.').pop()
  const username = eduPersonPrincipalName.split('@')[0]
  return `${username}_${typeCode}_ithenticate.${fileExtension}`
}

const getRenamedThesisSupplementaryFilename = (
  filename: string,
  eduPersonPrincipalName: string,
  typeCode: string,
  existingFiles: string[] = [],
): string => {
  const username = eduPersonPrincipalName.split('@')[0]

  // These are a little tricky because the file name is used in the name.
  if (filename.includes('_supplementary_') && filename.includes(`${username}_`)) {
    filename = filename.split('_supplementary_')[1]
  }

  // Check and replace if file name contains space
  if (filename.includes(' ')) {
    filename = filename.replace(/\s/g, '')
  }

  let uniqueFilename = `${username}_${typeCode}_supplementary_${filename}`

  if (existingFiles.length > 0) {
    let counter = 1
    const lastDotIndex = uniqueFilename.lastIndexOf('.')
    const namePart = lastDotIndex !== -1 ? uniqueFilename.substring(0, lastDotIndex) : uniqueFilename
    const extPart = lastDotIndex !== -1 ? uniqueFilename.substring(lastDotIndex) : ''

    while (existingFiles.includes(uniqueFilename)) {
      uniqueFilename = `${namePart}_${counter}${extPart}`
      counter++
    }
  }

  return uniqueFilename
}

const getRenamedThesisExaminerReviewFilename = (filename: string): string => {
  const fileExtension = filename.split('.').pop()
  return `examiner_review.${fileExtension}`
}

const getRenamedThesisExaminerAnnotatedThesisFilename = (filename: string): string => {
  const fileExtension = filename.split('.').pop()
  return `examiner_annotated_thesis.${fileExtension}`
}

const getRenamedThesisChairReviewFilename = (filename: string): string => {
  const fileExtension = filename.split('.').pop()
  return `chair_review.${fileExtension}`
}

const getRenamedThesisCorrectionsFilename = (
  filename: string,
  eduPersonPrincipalName: string,
  typeCode: string,
): string => {
  const fileExtension = filename.split('.').pop()
  const username = eduPersonPrincipalName.split('@')[0]
  return `${username}_${typeCode}_corrections.${fileExtension}`
}

export const getRenamedThesisFilename = (
  filename: string,
  fileType: ThesisFileType,
  eduPersonPrincipalName: string,
  thesisType: ThesisType,
  existingFiles: string[] = [],
): string | undefined => {
  const typeCode = getThesisTypeFileCode(thesisType)

  switch (fileType) {
    case 'THESIS_FILE':
      return getRenamedThesisDocumentFilename(filename, eduPersonPrincipalName, typeCode)
    case 'ITHENTICATE_FILE':
      return getRenamedThesisIthenticateFilename(filename, eduPersonPrincipalName, typeCode)
    case 'CORRECTIONS_FILE':
      return getRenamedThesisCorrectionsFilename(filename, eduPersonPrincipalName, typeCode)
    case 'EXAMINER_REVIEW_FILE':
      return getRenamedThesisExaminerReviewFilename(filename)
    case 'EXAMINER_ANNOTATED_THESIS_FILE':
      return getRenamedThesisExaminerAnnotatedThesisFilename(filename)
    case 'CHAIR_REVIEW_FILE':
      return getRenamedThesisChairReviewFilename(filename)
    case 'SUPPLEMENTARY_FILE':
      return getRenamedThesisSupplementaryFilename(filename, eduPersonPrincipalName, typeCode, existingFiles)
    default:
      return undefined
  }
}

export const getThesisFileTypeName = (fileType: ThesisFileType): string => {
  switch (fileType) {
    case 'THESIS_FILE':
      return 'Thesis file'
    case 'ITHENTICATE_FILE':
      return 'iThenticate file'
    case 'CORRECTIONS_FILE':
      return 'Corrections file'
    case 'EXAMINER_REVIEW_FILE':
      return 'Examiner review file'
    case 'EXAMINER_ANNOTATED_THESIS_FILE':
      return 'Examiner annotated thesis file'
    case 'CHAIR_REVIEW_FILE':
      return 'Chair review file'
    case 'SUPPLEMENTARY_FILE':
      return 'Supplementary file'
    default:
      return 'Unknown file type'
  }
}
